import AIChatProvider from '@/app/components/features/aiChat/AIChatContext';
import useEntryChat from '@/app/components/features/aiChat/hooks/useEntryChat';
import { Sidechat } from '@/app/components/shared/Sidechat';
import { useGetAllBidDocuments, useGetBidsList } from '@/app/hooks/queries/useBids';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import { useGetAllBidsIndexingStatus } from '@/app/hooks/useAllBidsIndexingStatus';
import useDocumentsIndexingStatus from '@/app/hooks/useDocumentsIndexingStatus';
import useEntityDocuments from '@/app/hooks/useEntityDocuments';
import { Main } from '@/app/ui/components/main';
import { ChatType, DocumentTypes, ProxyDocumentTypes } from '@/app/utils/constants';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Outlet } from '@tanstack/react-router';
import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/bids/_layout',
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { tenderPackageId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));

  const { data: bidsData } = useGetBidsList(tenderPackageId);

  const stream = useEntryChat({
    id: tenderPackageId,
    namespace: ChatType.EVALUATION,
    bidIds: bidsData?.data?.map((bid) => bid.id),
  });

  const allBidDocuments = useGetAllBidDocuments(tenderPackageId);
  const { data: documents } = useEntityDocuments(tenderPackageId, DocumentTypes.TENDER_PACKAGE);
  const hasTenderPackageDocs = documents && documents.length > 0;
  const { data: tpIndexingStatus, isLoading: isLoadingTPIndexing } = useDocumentsIndexingStatus({
    documentType: ProxyDocumentTypes.TENDER_PACKAGE,
    id: tenderPackageId,
    enabled: hasTenderPackageDocs,
  });
  const tpIndexed = (tpIndexingStatus?.indexedPercentage ?? 0) === 100;

  const { allBidsIndexed, isLoading: isLoadingBidsIndexing } =
    useGetAllBidsIndexingStatus(tenderPackageId);

  const areDocumentsIndexed = tpIndexed && allBidsIndexed;
  const isLoadingIndexingStatus = isLoadingTPIndexing || isLoadingBidsIndexing;

  return (
    <AIChatProvider
      chatType={ChatType.EVALUATION}
      sourceStream={stream}
      chatInitialOpen={false}
      evaluationName={evaluation.name}
      documents={allBidDocuments}
      documentsPending={isLoadingIndexingStatus}
      areDocumentsIndexed={areDocumentsIndexed}
    >
      <Main>
        <Outlet />
      </Main>
      <Sidechat />
    </AIChatProvider>
  );
}
